import { FormDetail } from '@b2b/types/forms';
import { CartOrderForm } from '@b2b/types/cart';
import { CartEventOrderForm } from '@b2b/types/event/api/event-vo';

export interface CartFormsModalsFilloutData {
  form: CartOrderForm;
  existingResponse?: FormResponseRecord;
  relatedItemNames?: string[];
  skipSaveInSessionStorage?: boolean;
}

/**
 * A single serialized form field (name/value pair), with optional
 * label and custom-field metadata added during processing.
 */
export interface FormField {
  name: string;
  value: string;
  label?: string;
  otherValue?: string;
  customFieldId?: string;
  customFieldLabel?: string;
  customFieldName?: string;
  values?: string[];
}

/**
 * A single element from the parsed sourceFormDefinition JSON array.
 */
export interface FormDefinitionElement {
  type: string;
  name: string;
  label: string;
  required?: boolean;
}

/**
 * Custom field entry as expected by the API.
 */
export interface FormResponseCustomField {
  id?: string;
  name: string;
  label?: string;
  fieldLabel?: string;
  value: string;
  fieldValue?: string;
}

/**
 * The result of processResponse – responseData ready for the API and
 * extracted custom-field entries.
 */
export interface ProcessedFormResponse {
  responseData: { fields: FormField[] };
  customFields: FormResponseCustomField[];
}

/**
 * Payload sent to apiService.form.responses.create.
 * Shape mirrors the fields consumed by the BE forms/responses endpoint.
 */
export interface FormResponsePayload {
  formId: string;
  name: string;
  type: string;
  source: string;
  number?: string;
  sourceFormId?: string;
  sourceFormResponseId?: string;
  sourceFormUploads?: string[];
  customFields?: FormResponseCustomField[];
  responseData: { fields: FormField[] };
  submissionDate?: string;
  formCategoryType?: string;
  displayOrder?: number;
}

/**
 * FormDetail extended with the `number` field returned by the API
 * but not yet reflected in the shared FormDetail interface.
 */
export type FormDetails = FormDetail & { number?: string };

/**
 * The publishedFormTemplate object returned inside a form response.
 */
export interface PublishedFormTemplate {
  id: string;
  name: string;
  type: string;
  source: string;
  sourceFormId: string;
  sourceFormUrl: string;
  sourceFormEditUrl: string;
  sourceFormDefinition: string;
  state: string;
  draftId: string;
  lastUpdatedDate: string;
  lastPublishedDate: string;
  oncePerCart: boolean;
  formCategoryType: string;
  displayOrder: number;
  formDisplaySettings: string;
}

export interface FormResponseRecordWithFormUUID extends FormResponseRecord {
  formUUID: string;
}

/**
 * The payload returned by POST /v2/forms/responses.
 */
export interface FormResponseRecord {
  id: string;
  formId: string;
  name: string;
  type: string;
  source: string;
  sourceFormId: string;
  customFields: FormResponseCustomField[];
  responseData: { fields: FormField[] };
  submissionDate: string;
  publishedFormTemplate: PublishedFormTemplate;
  formCategoryType: string;
  displayOrder: number;
}

export interface FormResponseDataRecord {
  formUUID: string;
  customerId: number;
  eventStartTime: string;
  formCategoryType: string;
  formId: string;
  formName: string;
  formNumber: string;
  formResponseData: FormResponseRecord;
  formResponseId: string;
  id: number;
  itemId: string;
  itemName: string;
  itemType: string;
  orderId: number;
}

/**
 * CartOrderForm with a unique UUID and optional response data
 */
export type OrderFormWithResponse = CartEventOrderForm & {
  uuid: string;
  response: FormResponseRecord | null;
  isRequired: boolean;
  formNumber?: string;
}

/**
 * Type guard to check if a form has a non-null response.
 */
export function isOrderFormWithResponse(form: OrderFormWithResponse): form is OrderFormWithResponse & { response: FormResponseRecord } {
  return form.response !== null && form.response !== undefined;
}
